'use client';

import { useRef } from 'react';
import { motion } from 'framer-motion';
import BookViewer from '@/components/ui/BookViewer';

export default function CatalogPage() {
  const pdfRef = useRef<HTMLDivElement>(null);


  const handleScrollToPdf = () => {
    pdfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100, damping: 15 },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 pb-16">
      <div className="relative overflow-hidden bg-white/90 pt-12 pb-16">
        <div className="container relative z-10 mx-auto px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-2xl rounded-xl border border-pink-100 bg-pink-50/80 p-10 shadow-md">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <div className="relative mb-2 flex justify-center">
                  <span className="rounded-full bg-primary px-3 py-1 text-sm font-medium text-white">
                    FİLİZ DEKORASYON
                  </span>
                </div>
                <h1 className="mb-6 text-center text-5xl font-black leading-tight tracking-tighter text-gray-900 md:text-6xl">
                  Dijital <span className="text-primary">Katalogumuz</span>
                </h1>
                <p className="mx-auto mb-8 max-w-xl text-center text-lg font-light text-gray-700 md:text-xl">
                  Berber ve kuaför salonları için profesyonel tasarlanmış ürün katalogumuzda
                  <span className="font-medium"> tüm çözümlerimizi bir arada</span> bulabilirsiniz.
                </p>

                <div className="flex flex-wrap justify-center gap-4">
                  <button
                    type="button"
                    className="flex items-center rounded-lg bg-primary px-6 py-3 font-medium text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-primary/30"
                    onClick={handleScrollToPdf}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Kataloğu Görüntüle
                  </button>

                  <a
                    href="/catalog/katalog.pdf"
                    download="filiz-kozmetik-katalog.pdf"
                    className="flex items-center rounded-lg border-2 border-primary px-6 py-3 font-medium text-primary transition-colors hover:bg-primary hover:text-white"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Kataloğu İndir
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto -mt-8 px-4">
        <motion.div
          ref={pdfRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-10 rounded-lg bg-white p-4 shadow-md"
        >
          <div className="mb-4 flex flex-col justify-between rounded-lg bg-gray-50 p-3 md:flex-row md:items-center">
            <div>
              <h3 className="text-lg font-medium text-primary-dark">Filiz Kozmetik Kataloğu</h3>
              <p className="mt-1 text-sm text-gray-600">
                Berber ve kuaför salonları için profesyonel ve konforlu ekipmanlar.
              </p>
            </div>

            <a
              href="/catalog/katalog.pdf"
              download="filiz-kozmetik-katalog.pdf"
              className="mt-3 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-sm text-white shadow-sm transition-colors hover:bg-primary-dark md:mt-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              PDF İndir
            </a>
          </div>

          <div className="w-full">
            <BookViewer 
              pdfUrl="/catalog/katalog.pdf" 
              downloadUrl="/catalog/katalog.pdf"
              fileName="filiz-kozmetik-katalog.pdf"
            />
          </div>
        </motion.div>


        <div className="mb-16 rounded-xl bg-white p-6 shadow-xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '30+', l: 'Ürün Kategorisi' },
              { n: '500+', l: 'Farklı Model' },
              { n: '31', l: 'Yıllık Tecrübe' },
              { n: '%100', l: 'Müşteri Memnuniyeti' },
            ].map((stat) => (
              <div
                key={stat.l}
                className="rounded-lg bg-primary/5 p-6 text-center transition-colors hover:bg-primary/10"
              >
                <div className="mb-2 text-4xl font-bold text-primary">{stat.n}</div>
                <div className="text-gray-600">{stat.l}</div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-primary-dark">Ürün Kataloğumuz</h2>
            <p className="mx-auto max-w-2xl text-gray-600">
              Filiz Kozmetik olarak, berber ve kuaför salonları için geniş ürün yelpazemizi tek bir katalogda
              sunmaktan gurur duyuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <motion.div variants={itemVariants} className="rounded-xl bg-white p-6 shadow-lg transition-shadow hover:shadow-xl">
              <div className="mb-4 flex justify-center text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="mb-3 text-center text-xl font-semibold text-primary-dark">Kapsamlı İçerik</h3>
              <p className="text-center text-gray-600">
                Kataloğumuzda tüm ürün kategorilerimizi ve modellerimizi detaylı teknik bilgilerle birlikte bulabilirsiniz.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="rounded-xl bg-white p-6 shadow-lg transition-shadow hover:shadow-xl">
              <div className="mb-4 flex justify-center text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h3 className="mb-3 text-center text-xl font-semibold text-primary-dark">Görsel Zenginlik</h3>
              <p className="text-center text-gray-600">
                Yüksek kaliteli ürün görselleri ve gerçek kullanım fotoğrafları ile ürünlerimizi yakından inceleyebilirsiniz.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="rounded-xl bg-white p-6 shadow-lg transition-shadow hover:shadow-xl">
              <div className="mb-4 flex justify-center text-primary">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mb-3 text-center text-xl font-semibold text-primary-dark">Fiyat Listesi</h3>
              <p className="text-center text-gray-600">
                Güncel fiyat listemiz ve ödeme seçenekleri katalogumuzda yer almaktadır. Toplu alım indirimleri için bizimle
                iletişime geçin.
              </p>
            </motion.div>
          </div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-primary-dark">Ürün Kategorilerimiz</h2>
            <p className="mx-auto max-w-2xl text-gray-600">
              Katalogumuzda yer alan geniş ürün yelpazemizi keşfedin. Tüm ihtiyaçlarınız için kaliteli ve şık çözümler
              sunuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {[
              'Berber Koltukları',
              'Berber Tezgahları',
              'Kuaför Tezgahları',
              'Makyaj Koltukları',
              'Tarama Koltukları',
              'Çocuk Koltukları',
              'Çocuk Tezgahları',
              'Bekleme Koltukları',
              'Yıkama Setleri',
              'Yardımcı Ekipmanlar',
              'Laboratuvar Dolapları',
              'Spa Ürünleri',
            ].map((category, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="rounded-lg border border-gray-200 bg-white p-4 text-center transition-all hover:border-primary hover:shadow-md"
              >
                <div className="mb-1 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="font-medium text-gray-800">{category}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="relative mt-16 overflow-hidden rounded-xl bg-primary/10 p-8">
          <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-primary/10" aria-hidden />
          <div className="absolute -left-10 -top-10 h-32 w-32 rounded-full bg-primary/10" aria-hidden />

          <div className="relative z-10 mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-2xl font-bold text-primary-dark md:text-3xl">Özel Teklif mi Almak İstiyorsunuz?</h2>
            <p className="mb-6 text-gray-600">
              Toplu siparişler ve özel projeler için size özel fiyatlandırma sunuyoruz. Hemen bizimle iletişime geçin ve
              ihtiyaçlarınızı paylaşın.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <a
                href="/iletisim"
                className="flex items-center justify-center rounded-lg bg-primary px-6 py-3 font-medium text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-primary/30"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                İletişime Geç
              </a>
              <a
                href="/toplu-teklif"
                className="flex items-center justify-center rounded-lg border-2 border-primary px-6 py-3 font-medium text-primary transition-colors hover:bg-primary hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Toplu Teklif Al
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
